/**
 * Invoice feature constants
 */

import type { InvoiceType } from '@/graphql';

/**
 * Receipt document type constant
 * Used to identify receipt documents (simplified tax documents)
 */
export const RECEIPT_DOCUMENT_TYPE: InvoiceType = 'RECEIPT';

/**
 * Standard invoice document type constant
 */
export const INVOICE_DOCUMENT_TYPE: InvoiceType = 'INVOICE';

/**
 * Credit note document type constant
 */
export const CREDIT_NOTE_DOCUMENT_TYPE: InvoiceType = 'CREDIT_NOTE';

/**
 * Debit note document type constant
 */
export const DEBIT_NOTE_DOCUMENT_TYPE: InvoiceType = 'DEBIT_NOTE';

/**
 * Proforma invoice document type constant
 */
export const PROFORMA_DOCUMENT_TYPE: InvoiceType = 'PROFORMA';

/**
 * Advance payment invoice document type constant
 */
export const ADVANCE_DOCUMENT_TYPE: InvoiceType = 'ADVANCE';

// ─── Formatting ───────────────────────────────────────────────────────────────

/**
 * Standard Intl.NumberFormat options for monetary amounts (2 decimal places).
 * The locale is provided at runtime via UIProvider / useFormatLocale().
 */
export const CURRENCY_FORMAT_OPTIONS = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
} as const;

// ─── Toast durations ──────────────────────────────────────────────────────────

/**
 * Toast notification display durations in milliseconds.
 */
export const TOAST_DURATION = {
  /** Short confirmation for non-destructive successes (saves, updates). */
  SUCCESS: 2000,
  /** Informational messages that don't require immediate action. */
  INFO: 3000,
  /** Longer display so users can read error details. */
  ERROR: 4000
} as const;

// ─── History ──────────────────────────────────────────────────────────────────

/**
 * Invoice fields that each produce their own dedicated history event type
 * (STATUS_EVENT_MAP on the backend). UPDATED entries whose `changes` object
 * contains ONLY these keys are redundant noise — the status transition is
 * already shown by its dedicated event card.
 *
 * Keep in sync with the backend's STATUS_EVENT_MAP:
 *   status           → APPROVAL_STATUS_CHANGED
 *   validationStatus → VALIDATION_STATUS_CHANGED
 *   paymentStatus    → PAYMENT_STATUS_CHANGED
 */
export const STATUS_EVENT_FIELDS = new Set<string>(['status', 'validationStatus', 'paymentStatus']);

/** Invoice fields whose values are enum tokens. Free-text fields must NOT appear here. */
export const ENUM_TYPED_FIELDS = new Set<string>(['documentType', 'paymentMethod', 'vatMode']);

/** Maps scalar history field names to their i18n enum namespace. */
export const FIELD_ENUM_MAP: Record<string, string> = {
  status: 'invoiceStatus',
  documentType: 'invoiceType',
  paymentMethod: 'paymentMethod',
  vatMode: 'vatMode',
  invoicePurpose: 'invoicePurpose',
  isVatDocument: 'boolean',
  postingStatus: 'invoicePostingStatus',
  contractLinkSource: 'contractLinkSource'
};

/** Maps status-change event types to their i18n enum namespace. */
export const EVENT_TYPE_ENUM_MAP: Record<string, string> = {
  APPROVAL_STATUS_CHANGED: 'invoiceStatus',
  VALIDATION_STATUS_CHANGED: 'validationStatus',
  PAYMENT_STATUS_CHANGED: 'paymentStatus'
};

/** What an approval change moves on the detail: the invoice and its timeline. */
export const APPROVAL_REFETCH_QUERIES = ['Invoice', 'GetInvoiceHistory'];
